import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  CheckCircle as ConnectedIcon,
  Cancel as DisconnectedIcon,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import AdminPage from "../AdminPage";
import { apiClient } from "../../utils/apiClient";
import { openEmailModal, openShipmentModal, openDatabaseObjectModal } from "../../store/adminSlice";
import { ResultsTable } from "./components/ResultsTable";
import { ResultDetailDrawer } from "./components/ResultDetailDrawer";
import { TinyValueComponent } from "../components/TinyValueComponent";
import type { PipelineResultLight, PipelineResultFull } from "./components/types";
import type {
  CheckInboxStatusResponse,
  InboxStatus,
  ListProductionResultsBody,
  InboxEntry,
} from "@services/emailAutomationPipeline/types";

interface ProcessedEntity {
  id: string;
  inbox: string;
  sharedInbox?: string | null;
  messageId: string;
  messageHeaderId: string;
  threadId?: string | null;
  sentTime: string;
  processedState: string;
  processingStartTime?: string | null;
  processingEndTime?: string | null;
  errorMessage?: string | null;
  workflowName: string;
  automationConfigId?: string | null;
}

interface ProductionEmailResult extends PipelineResultLight {
  resultId: string;
  runAt: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const PAGE_SIZE = 50;

export function EmailAutomationPipelineMonitoringPage() {
  const navigate = useNavigate();
  const { configId } = useParams<{ configId: string }>();
  const dispatch = useDispatch();

  // Config info
  const [configName, setConfigName] = useState("");
  const [configInboxes, setConfigInboxes] = useState<
    { userEmail: string; sharedInbox?: string; label: string }[]
  >([]);

  // Inbox status
  const [inboxStatuses, setInboxStatuses] = useState<InboxStatus[]>([]);
  const [checkingInboxes, setCheckingInboxes] = useState(false);

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Results tab
  const [results, setResults] = useState<ProductionEmailResult[]>([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [selectedResult, setSelectedResult] = useState<ProductionEmailResult | null>(null);
  const [detailedResult, setDetailedResult] = useState<PipelineResultFull | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Entities tab
  const [entities, setEntities] = useState<ProcessedEntity[]>([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [totalEntities, setTotalEntities] = useState(0);
  const [hasMoreEntities, setHasMoreEntities] = useState(false);

  // Filters — filterInboxIdx is the index into configInboxes, "" means all
  const [filterInboxIdx, setFilterInboxIdx] = useState<string>("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");

  // Helper to compute the selected inbox entry from current filter state
  const getSelectedInbox = (
    idxOverride?: string,
    inboxesOverride?: typeof configInboxes
  ) => {
    const idx = idxOverride ?? filterInboxIdx;
    const inboxes = inboxesOverride ?? configInboxes;
    return idx !== "" ? inboxes[Number(idx)] : undefined;
  };

  const selectedInbox = getSelectedInbox();

  // Load config info and initial data
  useEffect(() => {
    if (!configId) return;
    (async () => {
      try {
        const res = await apiClient.post<{
          config: { name: string; inboxes?: InboxEntry[] | null };
        }>(`/admin/email-automation/config/get`, { id: configId });
        setConfigName(res.config.name);
        // Extract structured inbox entries for the dropdown
        const inboxEntries: typeof configInboxes = [];
        if (res.config.inboxes) {
          for (const entry of res.config.inboxes) {
            if (typeof entry === "string") {
              inboxEntries.push({ userEmail: entry, label: entry });
            } else {
              inboxEntries.push({
                userEmail: entry.userEmail,
                sharedInbox: entry.sharedInbox,
                label: entry.sharedInbox
                  ? `${entry.sharedInbox} (${entry.userEmail})`
                  : entry.userEmail,
              });
            }
          }
        }
        setConfigInboxes(inboxEntries);
      } catch (error) {
        console.error("Error loading config:", error);
      }
    })();
    loadResults(0, false);
    loadEntities(0, false);
    handleCheckInboxStatus();
  }, [configId]);

  // Reload both when filters change
  useEffect(() => {
    if (configId) {
      const inbox = getSelectedInbox();
      loadResults(0, false, inbox);
      loadEntities(0, false, inbox);
    }
  }, [filterInboxIdx, filterStartDate, filterEndDate]);

  const handleCheckInboxStatus = async () => {
    if (!configId) return;
    setCheckingInboxes(true);
    try {
      const res = await apiClient.post<CheckInboxStatusResponse>(
        `/admin/email-automation/monitoring/check-inbox-status`,
        { configId }
      );
      setInboxStatuses(res.inboxes);
    } catch (error) {
      console.error("Error checking inbox status:", error);
    } finally {
      setCheckingInboxes(false);
    }
  };

  const loadResults = async (
    offset = 0,
    append = false,
    inboxOverride?: typeof configInboxes[number]
  ) => {
    if (!configId) return;
    const inbox = inboxOverride !== undefined ? inboxOverride : selectedInbox;
    setLoadingResults(true);
    try {
      const body: ListProductionResultsBody = {
        limit: PAGE_SIZE,
        offset,
        configId,
        inbox: inbox?.userEmail || undefined,
        sharedInbox: inbox?.sharedInbox || undefined,
        startDate: filterStartDate || undefined,
        endDate: filterEndDate || undefined,
      };
      const res = await apiClient.post<{
        results: ProductionEmailResult[];
        total: number;
        hasMore: boolean;
      }>(`/admin/email-automation/results/list-production`, body);

      if (append) {
        setResults((prev) => [...prev, ...res.results]);
      } else {
        setResults(res.results);
      }
      setHasMoreResults(res.hasMore);
    } catch (error) {
      console.error("Error loading results:", error);
    } finally {
      setLoadingResults(false);
    }
  };

  const loadEntities = async (
    offset = 0,
    append = false,
    inboxOverride?: typeof configInboxes[number]
  ) => {
    if (!configId) return;
    const inbox = inboxOverride !== undefined ? inboxOverride : selectedInbox;
    setLoadingEntities(true);
    try {
      const res = await apiClient.post<{
        entities: ProcessedEntity[];
        total: number;
        hasMore: boolean;
      }>(`/admin/email-automation/monitoring/list-processed-entities`, {
        configId,
        inbox: inbox?.userEmail || undefined,
        sharedInbox: inbox?.sharedInbox || undefined,
        limit: PAGE_SIZE,
        offset,
        startDate: filterStartDate || undefined,
        endDate: filterEndDate || undefined,
      });

      if (append) {
        setEntities((prev) => [...prev, ...res.entities]);
      } else {
        setEntities(res.entities);
      }
      setTotalEntities(res.total);
      setHasMoreEntities(res.hasMore);
    } catch (error) {
      console.error("Error loading entities:", error);
    } finally {
      setLoadingEntities(false);
    }
  };

  const handleResultRowClick = async (result: PipelineResultLight, index: number) => {
    const prodResult = result as ProductionEmailResult;
    setSelectedResult(prodResult);
    setDrawerOpen(true);
    setLoadingDetails(true);
    try {
      const res = await apiClient.post<{ emailResult: PipelineResultFull }>(
        `/admin/email-automation/results/get-automation-result`,
        { resultId: prodResult.resultId, input: prodResult.input, resultIndex: index }
      );
      setDetailedResult(res.emailResult);
    } catch (error) {
      console.error("Error loading result details:", error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleOpenEmail = (emailId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch(openEmailModal(emailId));
  };

  const handleOpenShipment = (shipmentId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch(openShipmentModal(shipmentId));
  };

  const handleOpenReminder = (reminderId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch(openDatabaseObjectModal({ table: "shipmentReminder", id: reminderId }));
  };

  const getStateColor = (state: string) => {
    switch (state) {
      case "PROCESSED": return "success";
      case "PROCESSING": return "info";
      case "ERRORED": return "error";
      case "NOT_PROCESSED": return "default";
      default: return "default";
    }
  };

  return (
    <AdminPage title={`Monitoring: ${configName || "..."}`}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              startIcon={<BackIcon />}
              onClick={() =>
                navigate(
                  configId
                    ? `/admin/email-automation/config/${configId}`
                    : "/admin/email-automation"
                )
              }
            >
              Back
            </Button>
            <Typography variant="h5">
              Monitoring{configName ? `: ${configName}` : ""}
            </Typography>
          </Box>
        </Box>

        {/* Inbox Status Section */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Inbox Status</Typography>
            <Button
              variant="outlined"
              startIcon={checkingInboxes ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleCheckInboxStatus}
              disabled={checkingInboxes}
            >
              {checkingInboxes ? "Checking..." : "Check Status"}
            </Button>
          </Box>

          {inboxStatuses.length === 0 && checkingInboxes && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          )}

          {inboxStatuses.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Last Email</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inboxStatuses.map((inbox, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Typography variant="body2">{inbox.email}</Typography>
                      </TableCell>
                      <TableCell>
                        {inbox.sharedInbox ? (
                          <Chip label={`Shared: ${inbox.sharedInbox}`} size="small" variant="outlined" />
                        ) : (
                          <Chip label="Personal" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        {inbox.connected ? (
                          <Chip
                            icon={<ConnectedIcon />}
                            label="Connected"
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip
                            icon={<DisconnectedIcon />}
                            label={inbox.error ? `Error: ${inbox.error.slice(0, 50)}` : "Disconnected"}
                            color="error"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {inbox.provider && (
                          <Chip
                            label={inbox.provider}
                            size="small"
                            color={inbox.provider === "google" ? "primary" : "secondary"}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {inbox.lastEmailDate && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {new Date(inbox.lastEmailDate).toLocaleString()}
                          </Typography>
                        )}
                        {inbox.lastEmailSubject && (
                          <Typography variant="body2" sx={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {inbox.lastEmailSubject}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Global Filters */}
        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Filter by Inbox</InputLabel>
            <Select
              value={filterInboxIdx}
              label="Filter by Inbox"
              onChange={(e) => setFilterInboxIdx(e.target.value)}
            >
              <MenuItem value="">All Inboxes</MenuItem>
              {configInboxes.map((entry, idx) => (
                <MenuItem key={idx} value={String(idx)}>
                  {entry.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Start Date"
            type="date"
            value={filterStartDate}
            onChange={(e) => setFilterStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            label="End Date"
            type="date"
            value={filterEndDate}
            onChange={(e) => setFilterEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        {/* Tabs: Results & Entities */}
        <Paper sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
              <Tab label="Results" />
              <Tab label="Processed Entities" />
            </Tabs>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => { loadResults(); loadEntities(); }}
            >
              Refresh
            </Button>
          </Box>

          {/* Results Tab */}
          <TabPanel value={tabValue} index={0}>
            {loadingResults ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : results.length === 0 ? (
              <Typography color="text.secondary" py={2}>
                No production results found for this configuration.
              </Typography>
            ) : (
              <>
                <ResultsTable
                  results={results}
                  selectedEmailId={selectedResult?.emailId}
                  onRowClick={handleResultRowClick}
                  onOpenEmail={handleOpenEmail}
                  onOpenShipment={handleOpenShipment}
                  onOpenReminder={handleOpenReminder}
                />
                {hasMoreResults && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Button onClick={() => loadResults(results.length, true)}>
                      Load More
                    </Button>
                  </Box>
                )}
              </>
            )}
          </TabPanel>

          {/* Entities Tab */}
          <TabPanel value={tabValue} index={1}>
            {loadingEntities ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : entities.length === 0 ? (
              <Typography color="text.secondary" py={2}>
                No processed entities found for this configuration.
              </Typography>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Total: {totalEntities} entities
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Inbox</TableCell>
                        <TableCell>Message ID</TableCell>
                        <TableCell>State</TableCell>
                        <TableCell>Sent Time</TableCell>
                        <TableCell>Processing Time</TableCell>
                        <TableCell>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {entities.map((entity) => (
                        <TableRow key={entity.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {entity.sharedInbox
                                ? `${entity.sharedInbox} (${entity.inbox})`
                                : entity.inbox}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <TinyValueComponent value={entity.messageHeaderId} clipboard />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={entity.processedState}
                              size="small"
                              color={getStateColor(entity.processedState) as any}
                            />
                          </TableCell>
                          <TableCell>
                            {new Date(entity.sentTime).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            {entity.processingStartTime && entity.processingEndTime
                              ? `${((new Date(entity.processingEndTime).getTime() - new Date(entity.processingStartTime).getTime()) / 1000).toFixed(1)}s`
                              : entity.processingStartTime
                                ? "In progress..."
                                : "-"}
                          </TableCell>
                          <TableCell>
                            {entity.errorMessage && (
                              <Typography variant="body2" color="error" sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {entity.errorMessage}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {hasMoreEntities && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Button onClick={() => loadEntities(entities.length, true)}>
                      Load More
                    </Button>
                  </Box>
                )}
              </>
            )}
          </TabPanel>
        </Paper>

        {/* Result Detail Drawer */}
        <ResultDetailDrawer
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setSelectedResult(null);
            setDetailedResult(null);
          }}
          selectedResult={selectedResult}
          detailedResult={detailedResult}
          loadingDetails={loadingDetails}
          onOpenEmail={handleOpenEmail}
          resultId={selectedResult?.resultId}
        />
      </Box>
    </AdminPage>
  );
}
